// src/js/modules/tabs.js
// Flowbite configuration + ScrollIntoView (robust) für alle .tabs
import { initFlowbite } from 'flowbite/lib/esm/components/index.js';
import { debounce } from '../utils/debounce.js';
import { scrollIntoViewHoriz } from '../utils/horizontal-scroll.js';

const FOCUSABLE_IN_PANEL = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const isArrowDownKey = (event) => event.key === 'ArrowDown' || event.key === 'Down';
const isArrowUpKey = (event) => event.key === 'ArrowUp' || event.key === 'Up';

function refreshSwipersInPanel(panel) {
  if (!panel) return;

  requestAnimationFrame(() => {
    const swipers = new Set();

    if (panel.classList?.contains('swiper') && panel.swiper) {
      swipers.add(panel.swiper);
    }

    panel.querySelectorAll('.swiper').forEach((element) => {
      if (element.swiper) {
        swipers.add(element.swiper);
      }
    });

    swipers.forEach((swiper) => {
      try {
        swiper.update();
        swiper.updateSlides();
        swiper.updateProgress();
        swiper.updateSlidesClasses();

        if (swiper.navigation) {
          swiper.navigation.destroy();
          swiper.navigation.init();
          swiper.navigation.update();
        }

        if (swiper.pagination) {
          swiper.pagination.render();
          swiper.pagination.update();
        }
      } catch (error) {
        // Ignore individual swiper update failures to avoid breaking tab logic.
      }
    });
  });
}

export default function initTabs() {
  sanitizeFlowbiteModalTargets();

  if (!document.body.classList.contains('wp-admin')) {
    sanitizeFlowbiteTabTargets();
  }

  initScopedNestedTabs();

  // alle .tabs Container durchlaufen
  document.querySelectorAll('.tabs').forEach((tabsRoot) => {
    if (tabsRoot.classList.contains('tabs--nested')) return;

    const listEl = tabsRoot.querySelector('[data-tabs-toggle]');
    if (!listEl) return;

    // Mehrfach-Init verhindern
    if (listEl.dataset.tabsInit === '1') return;
    listEl.dataset.tabsInit = '1';

    // Optional: sicherstellen, dass alle Trigger die Basis-Klasse tragen
    const tabs = Array.from(listEl.querySelectorAll('[role="tab"][data-tabs-target]'));
    tabs.forEach((btn) => {
      if (!btn.classList.contains('tabs__btn')) btn.classList.add('tabs__btn');
    });

    // Scroll-into-view (robust, via scrollLeft) an die Tab-Leiste hängen
    initScrollIntoView(listEl, tabs);

    // Tastatur-UX (WAI-ARIA empfohlen)
    initKeyboardNavigation(listEl, tabs);
  });

  // Flowbite anhand unserer Data-Attributes initialisieren
  initFlowbite();

  activateVehicleSearchTabFromUrl();

  // Animated Tabs: Panels smooth ein-/ausblenden
  initAnimatedTabs();

  // Custom Subtabs initialisieren
  initFahrzeugSubtabs();
}

function activateVehicleSearchTabFromUrl() {
  let params;
  let requestedTab;
  let tabButton;
  const supportedTabs = ['fahrzeugsuche', 'haendlersuche'];

  try {
    params = new URLSearchParams(window.location.search || '');
  } catch (error) {
    return;
  }

  requestedTab = (params.get('tab') || '').trim();

  if (!supportedTabs.includes(requestedTab)) return;

  tabButton = document.getElementById(requestedTab);

  if (!tabButton || tabButton.getAttribute('role') !== 'tab') return;

  window.requestAnimationFrame(() => {
    if (tabButton.getAttribute('aria-selected') === 'true') return;
    tabButton.click();
    window.requestAnimationFrame(() => {
      if (document.activeElement === tabButton) {
        tabButton.blur();
      }
    });
  });
}

/**
 * Entfernt fehlerhafte Flowbite-Modal-Targets, wenn das referenzierte Modal
 * im aktuellen DOM nicht vorhanden ist (z. B. im Gutenberg-Editor).
 */
function sanitizeFlowbiteModalTargets() {
  document
    .querySelectorAll('[data-modal-target], [data-modal-toggle]')
    .forEach((trigger) => {
      const targetId = trigger.getAttribute('data-modal-target') || trigger.getAttribute('data-modal-toggle');
      if (!targetId) return;

      const modalEl = document.getElementById(targetId);
      if (modalEl) return;

      trigger.removeAttribute('data-modal-target');
      trigger.removeAttribute('data-modal-toggle');
    });
}

/**
 * Flowbite bricht mit "Cannot read properties of null (reading 'classList')",
 * wenn ein data-tabs-target auf kein bestehendes Panel zeigt.
 * Daher werden fehlerhafte Targets vor initFlowbite() bereinigt.
 */
function sanitizeFlowbiteTabTargets() {
  document.querySelectorAll('[data-tabs-toggle]').forEach((listEl) => {
    const tabsRoot = listEl.closest('.tabs') || document;
    const localPanels = Array.from(tabsRoot.querySelectorAll('.tabs__panels .tabs__panel'));
    const tabs = Array.from(listEl.querySelectorAll('[role="tab"]'));
    tabs.forEach((tab, index) => {
      const targetSelector = tab.getAttribute('data-tabs-target');
      if (!targetSelector) {
        disableInvalidFlowbiteTab(tab);
        return;
      }

      let targetPanel = null;
      try {
        targetPanel = document.querySelector(targetSelector);
      } catch (e) {
        targetPanel = null;
      }

      // Fallback über aria-controls (ID) versuchen
      if (!targetPanel) {
        const controlsId = tab.getAttribute('aria-controls');
        if (controlsId) {
          targetPanel = document.getElementById(controlsId);
          if (targetPanel) {
            tab.setAttribute('data-tabs-target', `#${controlsId}`);
          }
        }
      }

      // Fallback: gleichindexiges Panel im selben Tabs-Container verwenden
      if (!targetPanel) {
        const panelByIndex = localPanels[index] || null;
        if (panelByIndex) {
          let panelId = panelByIndex.id;
          if (!panelId) {
            panelId = `tabs-panel-fallback-${index + 1}-${Math.random().toString(36).slice(2, 7)}`;
            panelByIndex.id = panelId;
          }

          tab.setAttribute('data-tabs-target', `#${panelId}`);
          tab.setAttribute('aria-controls', panelId);

          if (!panelByIndex.getAttribute('aria-labelledby')) {
            let tabId = tab.id;
            if (!tabId) {
              tabId = `tabs-tab-fallback-${index + 1}-${Math.random().toString(36).slice(2, 7)}`;
              tab.id = tabId;
            }
            panelByIndex.setAttribute('aria-labelledby', tabId);
          }

          targetPanel = panelByIndex;
        }
      }

      // Wenn kein valides Ziel existiert, aus Flowbite-Targeting entfernen
      if (!targetPanel) {
        disableInvalidFlowbiteTab(tab);
      }
    });
  });
}

function disableInvalidFlowbiteTab(tab) {
  tab.removeAttribute('data-tabs-target');
  tab.removeAttribute('aria-controls');
  tab.removeAttribute('aria-selected');
  tab.removeAttribute('role');
}

function initScopedNestedTabs() {
  document.querySelectorAll('.tabs--nested').forEach((root) => {
    const listEl = root.querySelector('[data-tabs-toggle], [data-carthago-nested-tabs]');
    if (!listEl || listEl.dataset.nestedTabsInit === '1') return;

    const activeClasses = (listEl.dataset.tabsActiveClasses || 'tabs__btn--active').split(/\s+/).filter(Boolean);
    const inactiveClasses = (listEl.dataset.tabsInactiveClasses || 'tabs__btn--inactive').split(/\s+/).filter(Boolean);
    const panelSelector = listEl.getAttribute('data-tabs-toggle') || '';
    const panelsRoot = panelSelector ? root.querySelector(panelSelector) : root.querySelector('.tabs__panels');
    const tabs = Array.from(listEl.querySelectorAll('[role="tab"]'));
    const panels = panelsRoot ? Array.from(panelsRoot.children).filter((panel) => panel.classList.contains('tabs__panel')) : [];

    if (!tabs.length || !panels.length) return;

    listEl.dataset.nestedTabsInit = '1';
    listEl.dataset.carthagoNestedTabs = '1';
    listEl.removeAttribute('data-tabs-toggle');

    const getPanel = (tab, index) => {
      const targetSelector = tab.dataset.nestedTabsTarget || tab.getAttribute('data-tabs-target') || '';
      tab.dataset.nestedTabsTarget = targetSelector;

      if (targetSelector) {
        try {
          const localPanel = root.querySelector(targetSelector);
          if (localPanel) return localPanel;
        } catch (error) {
          // Fallback below.
        }
      }

      const controlsId = tab.getAttribute('aria-controls');
      if (controlsId) {
        const localPanel = panels.find((panel) => panel.id === controlsId);
        if (localPanel) return localPanel;
      }

      return panels[index] || null;
    };

    const tabPanelPairs = tabs.map((tab, index) => ({
      tab,
      panel: getPanel(tab, index),
    })).filter(({ panel }) => panel);

    tabs.forEach((tab) => {
      if (!tab.dataset.tabLabel) {
        tab.dataset.tabLabel = (tab.textContent || '').trim();
      }
    });

    const setActive = (activeTab) => {
      tabPanelPairs.forEach(({ tab, panel }) => {
        const isActive = tab === activeTab;

        tab.classList.toggle('tabs__btn--active', isActive);
        tab.classList.toggle('tabs__btn--inactive', !isActive);
        activeClasses.forEach((className) => tab.classList.toggle(className, isActive));
        inactiveClasses.forEach((className) => tab.classList.toggle(className, !isActive));
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');

        panel.classList.toggle('hidden', !isActive);
        panel.toggleAttribute('hidden', !isActive);
        panel.classList.toggle('tabs__panel--visible', isActive);

        if (isActive) {
          refreshSwipersInPanel(panel);
        }
      });
    };

    const initiallyActive = tabPanelPairs.find(({ tab }) => tab.getAttribute('aria-selected') === 'true') || tabPanelPairs[0];
    setActive(initiallyActive.tab);

    tabPanelPairs.forEach(({ tab }) => {
      const onClick = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        setActive(tab);
      };

      tab.addEventListener('click', onClick, true);
      tab.addEventListener('click', onClick);
    });
  });
}

/**
 * ScrollIntoView für eine Tab-Leiste (listEl = Element mit role="tablist")
 * Nutzt scrollLeft für feinere Kontrolle als scrollIntoView
 */
function initScrollIntoView(listEl, tabs) {
  if (!tabs.length) return;

  const moveTo = (tabEl, align = 'nearest') => scrollIntoViewHoriz(tabEl, listEl, align);

  // Klick-Handling: aktiven + Nachbar-Tabs in Sicht holen
  tabs.forEach((tab, index) => {
    tab.addEventListener(
      'click',
      () => {
        // Flowbite setzt aria-selected async – wir warten einen Frame
        requestAnimationFrame(() => {
          moveTo(tab, 'nearest');
          const prevTab = tabs[index - 1];
          const nextTab = tabs[index + 1];
          if (prevTab) moveTo(prevTab, 'nearest');
          if (nextTab) moveTo(nextTab, 'nearest');
        });
      },
      { passive: true }
    );
  });

  // Beobachte aria-selected (falls Tabs per Script wechseln)
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName === 'aria-selected') {
        const el = m.target;
        if (el.getAttribute('aria-selected') === 'true') {
          moveTo(el, 'nearest');
        }
      }
    }
  });
  tabs.forEach((t) => mo.observe(t, { attributes: true }));

  // Beim Resize neu zentrieren (debounced)
  const onResize = debounce(() => {
    const currentActive = listEl.querySelector('[role="tab"][aria-selected="true"]');
    if (currentActive) moveTo(currentActive, 'nearest');
  }, 120);
  window.addEventListener('resize', onResize);
}

function initAnimatedTabs() {
  document.querySelectorAll('.tabs').forEach((root) => {
    const panels = Array.from(root.querySelectorAll('.tabs__panel'));
    if (!panels.length) return;

    const syncPanel = (panel) => {
      const isHidden = panel.hasAttribute('hidden') || panel.classList.contains('hidden');
      panel.classList.toggle('tabs__panel--visible', !isHidden);

      if (!isHidden) {
        refreshSwipersInPanel(panel);
      }
    };

    panels.forEach((panel) => {
      syncPanel(panel);

      if (panel.__animatedTabsObserver) return;

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'hidden' || mutation.attributeName === 'class') {
            syncPanel(mutation.target);
          }
        }
      });

      observer.observe(panel, { attributes: true, attributeFilter: ['hidden', 'class'] });
      panel.__animatedTabsObserver = observer;
    });
  });
}

function initFahrzeugSubtabs() {
  document.querySelectorAll('[data-fahrzeug-subtabs]').forEach((root) => {
    const triggers = Array.from(root.querySelectorAll('[data-subtab-target]'));
    if (!triggers.length) return;

    const panelMap = new Map();
    let activeTrigger = null;
    const VISIBLE_CLASS = 'fahrzeug-subtabs__panel--visible';
    const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const getPanel = (trigger) => {
      if (panelMap.has(trigger)) return panelMap.get(trigger);
      const targetSelector = trigger.dataset.subtabTarget;
      if (!targetSelector) return null;
      const panel = root.querySelector(targetSelector);
      if (panel) panelMap.set(trigger, panel);
      return panel || null;
    };

    const removeFocusTrap = (panel) => {
      if (!panel || !panel.__subtabFocusHandler) return;
      panel.removeEventListener('keydown', panel.__subtabFocusHandler);
      panel.__subtabFocusHandler = null;
      panel.__subtabFocusTrigger = null;
    };

    const setupFocusTrap = (panel, trigger) => {
      if (!panel) return;
      removeFocusTrap(panel);

      const handler = (event) => {
        if (event.key !== 'Tab') return;

        const focusables = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
          return !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden') && el.offsetParent !== null;
        });

        if (!focusables.length) {
          event.preventDefault();
          trigger.focus();
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;

        if (event.shiftKey) {
          if (active === first || !panel.contains(active)) {
            event.preventDefault();
            trigger.focus();
          }
        } else {
          if (active === last) {
            event.preventDefault();
            focusables[0].focus();
          }
        }
      };

      panel.addEventListener('keydown', handler);
      panel.__subtabFocusHandler = handler;
      panel.__subtabFocusTrigger = trigger;
    };

    const deactivate = (trigger) => {
      const panel = getPanel(trigger);
      if (!panel) return;
      trigger.classList.remove('fahrzeug-subtabs__trigger--active');
      trigger.setAttribute('aria-selected', 'false');
      trigger.setAttribute('aria-expanded', 'false');
      panel.classList.remove(VISIBLE_CLASS);
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.add('hidden');
      removeFocusTrap(panel);
    };

    const activate = (trigger) => {
      const panel = getPanel(trigger);
      if (!panel) return;
      trigger.classList.add('fahrzeug-subtabs__trigger--active');
      trigger.setAttribute('aria-selected', 'true');
      trigger.setAttribute('aria-expanded', 'true');
      panel.classList.remove('hidden');
      panel.setAttribute('aria-hidden', 'false');

      // Reflow sicherstellen, bevor wir die Sichtbarkeitsklasse setzen
      requestAnimationFrame(() => {
        panel.classList.add(VISIBLE_CLASS);
        refreshSwipersInPanel(panel);
      });

      setupFocusTrap(panel, trigger);
    };

    triggers.forEach((trigger, index) => {
      trigger.setAttribute('aria-selected', 'false');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('tabindex', '0');

      const panel = getPanel(trigger);
      if (panel) {
        panel.classList.add('hidden');
        panel.setAttribute('aria-hidden', 'true');
        panel.classList.remove(VISIBLE_CLASS);
        removeFocusTrap(panel);
      }

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const isActive = activeTrigger === trigger;
        if (activeTrigger) deactivate(activeTrigger);
        if (isActive) {
          activeTrigger = null;
        } else {
          activate(trigger);
          activeTrigger = trigger;
        }
      });

      trigger.addEventListener('keydown', (event) => {
        const key = event.key;
        if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          trigger.click();
          return;
        }

        if (key === 'ArrowDown') {
          event.preventDefault();
          const panel = getPanel(trigger);
          if (panel && !panel.classList.contains('hidden')) {
            const focusable = panel.querySelector(
              'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable) {
              focusable.focus();
              return;
            }
          }
        }

        if (key !== 'ArrowRight' && key !== 'ArrowLeft') return;
        event.preventDefault();
        const dir = key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + dir + triggers.length) % triggers.length;
        triggers[nextIndex].focus();
      });
    });
  });
}

/**
 * Tastatur-Navigation (Pfeile/Home/End)
 */
function getPanelForTab(tab) {
  const targetSelector = tab?.getAttribute('data-tabs-target');
  if (!targetSelector) return null;
  try {
    return document.querySelector(targetSelector);
  } catch (err) {
    return null;
  }
}

function focusFirstInPanel(panel) {
  if (!panel) return;
  const focusable = panel.querySelector(FOCUSABLE_IN_PANEL);
  if (focusable) {
    focusable.focus({ preventScroll: true });
    return;
  }

  if (!panel.hasAttribute('tabindex')) {
    panel.setAttribute('tabindex', '-1');
  }
  panel.focus({ preventScroll: true });
}

function bindPanelKeyboard(panel, tab) {
  if (!panel || panel.dataset.tabKeyboardBound === '1') return;
  panel.dataset.tabKeyboardBound = '1';
  const triggerId = tab?.id || '';

  panel.addEventListener('keydown', (event) => {
    if (isArrowUpKey(event)) {
      const trigger = triggerId ? document.getElementById(triggerId) : tab;
      if (trigger) {
        event.preventDefault();
        trigger.focus({ preventScroll: true });
      }
    }
  });
}

function initKeyboardNavigation(listEl, tabs) {
  listEl.addEventListener('keydown', (e) => {
    const current = document.activeElement;
    const i = tabs.indexOf(current);
    if (i === -1) return;

    let ni = i;
    if (e.key === 'ArrowRight') ni = Math.min(i + 1, tabs.length - 1);
    if (e.key === 'ArrowLeft') ni = Math.max(i - 1, 0);
    if (e.key === 'Home') ni = 0;
    if (e.key === 'End') ni = tabs.length - 1;

    if (isArrowDownKey(e)) {
      e.preventDefault();
      if (current.getAttribute('aria-selected') !== 'true') {
        current.click();
      }

      const panel = getPanelForTab(current);
      if (panel) {
        bindPanelKeyboard(panel, current);
        requestAnimationFrame(() => focusFirstInPanel(panel));
      }
      return;
    }

    if (ni !== i) {
      e.preventDefault();
      tabs[ni].focus();
      tabs[ni].click(); // Flowbite schaltet, Scroll folgt
      const panel = getPanelForTab(tabs[ni]);
      if (panel) bindPanelKeyboard(panel, tabs[ni]);
    }
  });

  tabs.forEach((tab) => {
    const panel = getPanelForTab(tab);
    if (panel) bindPanelKeyboard(panel, tab);
  });
}
